import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Input, PasswordDisplayIcon, PasswordHiddenIcon } from "../../components";
import "./SetPassword.scss";
import { passwordComplexityRequest, resetPasswordRequest } from "../../services/password";
import { ResetPasswordRequest } from "../../models/ResetPasswordRequest";
import "../../assets/toastify.scss";
import { PasswordComplexity } from "../../models/PasswordComplexity";
import { validatePasswordComplexity } from "../../validations/validations";

export const SetPassword = () => {
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [redirectUri, setRedirectUri] = useState<string>("/login");
  const [resetToken, setResetToken] = useState<string>("");
  const [triggerFocus, setTriggerFocus] = useState<boolean | undefined>(undefined);
  const [clearValue, setClearValue] = useState<boolean | undefined>(undefined);
  const [passwordComplexity, setPasswordComplexity] = useState<PasswordComplexity>();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    const path = searchParams.get("redirect_uri");
    if (path) {
      setRedirectUri(path);
    }

    const token = searchParams.get("reset_token");
    if (token) {
      setResetToken(token);
      void getPasswordComplexity(token);
    }

    // eslint-disable-next-line
  }, []);

  const getPasswordComplexity = async (token: string) => {
    await passwordComplexityRequest(token).then((response) => {
      if (response != null) {
        setPasswordComplexity(response);
      }
      return response;
    });
  };

  const newPasswordValidationMsg = (): string | null => {
    if (!validatePasswordComplexity(passwordComplexity, newPassword)) {
      return t("validationPasswordNotMeetComplexity");
    }
    return null;
  };

  const confirmNewPasswordValidationMsg = (): string | null => {
    if (!newPasswordValidationMsg()) {
      const areEqualPasswords = newPassword === confirmNewPassword;

      if (!areEqualPasswords) {
        return t("validationPasswordNotMatch");
      }
    }

    return null;
  };

  const isPasswordValid =
    newPassword === confirmNewPassword && validatePasswordComplexity(passwordComplexity, newPassword);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);

    const data: ResetPasswordRequest = {
      newPassword: newPassword,
      token: resetToken,
    };
    // eslint-disable-next-line
    await resetPasswordRequest(data).then((response) => {
      if (response === true) {
        navigate(redirectUri, {
          state: { showResetPasswordMessage: true },
        });
      } else {
        setNewPassword("");
        setConfirmNewPassword("");
        setClearValue(!clearValue);
        setTriggerFocus(!triggerFocus);
        setLoading(false);
      }

      return response;
    });
  };

  return (
    <div className="container">
      <h1 className="header">{t("setPassword")}</h1>
      <p className="password-content">{t("setPasswordMessage")}</p>
      <form className="set-password-elements" onSubmit={handleSubmit}>
        <Input
          placeholder={t("newPassword")}
          onChange={(e) => setNewPassword(e.target.value)}
          type={showNewPassword ? "text" : "password"}
          autofocus={true}
          triggerFocus={triggerFocus}
          validationMsg={newPasswordValidationMsg()}
          className="username-input"
          Icon={showNewPassword ? PasswordHiddenIcon : PasswordDisplayIcon}
          onIconClick={() => setShowNewPassword(!showNewPassword)}
          clearValue={clearValue}
        />
        <Input
          placeholder={t("confirmNewPassword")}
          onChange={(e) => setConfirmNewPassword(e.target.value)}
          type={showConfirmNewPassword ? "text" : "password"}
          autofocus={false}
          validationMsg={confirmNewPasswordValidationMsg()}
          className="username-input"
          Icon={showConfirmNewPassword ? PasswordHiddenIcon : PasswordDisplayIcon}
          onIconClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
          clearValue={clearValue}
        />
        {passwordComplexity?.validationExpressions && (
          <div className="password-rules">
            <p>{t("passwordMustInclude")}</p>
            <ul>
              {passwordComplexity?.validationExpressions.map((value, idx) => (
                <li key={idx}>{value.description}</li>
              ))}
            </ul>
          </div>
        )}
        <Button
          lKey={t("savePassword")}
          isDisabled={!isPasswordValid}
          className="username-button"
          type="submit"
          isLoading={loading}
        />
      </form>
    </div>
  );
};
